<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="0" rounded="xxl" class="app-card">
            <v-card-title>
              <AppPageHeader
                icon="mdi-package-variant"
                :title="isDeletePage() != true ? 'Products' : 'Deleted Products'"
                subtitle="What your company sells, with variants and stock"
                style="width: 100%"
              >
                <!-- No add button: new products are created from a purchase order -->
              </AppPageHeader>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="6">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search ... "
                    color="#e60040"
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  >
                  </v-text-field>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    ref="status"
                    solo
                    rounded
                    v-model="filter.is_active"
                    :items="statuses"
                    clearable
                    @click:clear="(filter.is_active = null), (page = 1)"
                    label="Status"
                    @input="getData"
                    prepend-inner-icon="mdi-toggle-switch"
                  ></v-autocomplete>
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  sm="3"
                  v-for="(level, levelIndex) in categoryLevels"
                  :key="`cat_level_${levelIndex}`"
                >
                  <v-autocomplete
                    solo
                    rounded
                    v-model="level.selected"
                    :items="level.items"
                    item-text="name"
                    item-value="id"
                    clearable
                    :label="getCategoryLabel(levelIndex)"
                    @input="onCategorySelect(levelIndex)"
                    prepend-inner-icon="mdi-shape"
                    :loading="level.loading"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col md="12">
          <v-card elevation="0" rounded="xxl" class="app-card">
            <v-card-text>
              <section v-if="isLoading" style="height: 400px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>
              <div v-else class="products-table-wrapper">
                <table class="products-table">
                  <thead>
                    <tr>
                      <th class="col-sr">Sr No</th>
                      <th class="col-status">Status</th>
                      <th class="col-name">Products</th>
                      <th class="col-hsn">HSN Code</th>
                      <th class="col-desc">Description</th>
                      <th class="col-category">Category Path</th>
                      <th class="col-qty">Qty</th>
                      <th class="col-delivery">Delivery Fee</th>
                      <th class="col-delivery">Delivery Charges</th>
                      <th class="col-action text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody v-if="products.length !== 0">
                    <tr
                      v-for="(product, i) in products"
                      :key="`product_${i}`"
                      :class="i % 2 === 0 ? 'row-even' : 'row-odd'"
                    >
                      <td class="col-sr">{{ i + 1 }}</td>
                      <td class="col-status">
                        <v-chip v-if="product.is_active == 1" small color="teal" text-color="white">
                          <v-avatar left><v-icon size="15">mdi-checkbox-marked-circle</v-icon></v-avatar>
                          Active
                        </v-chip>
                        <v-chip v-else small color="red" text-color="white">
                          <v-avatar left><v-icon size="15">mdi-close-circle</v-icon></v-avatar>
                          Inactive
                        </v-chip>
                      </td>
                      <td class="col-name">{{ product.name || "" }}</td>
                      <td class="col-hsn">{{ product.hsn_code || "" }}</td>
                      <td class="col-desc">
                        <span :title="product.description">{{ product.description || "" }}</span>
                      </td>
                      <td class="col-category">
                        <div v-if="product.category" class="category-chips">
                          <v-chip
                            v-for="(item, ci) in getCategoryPath(product.category)"
                            :key="ci"
                            x-small
                            color="primary"
                            text-color="white"
                            class="mr-1"
                          >{{ item.text }}</v-chip>
                          <span v-if="getCategoryPath(product.category).length > 1" class="arrow-sep">→</span>
                        </div>
                        <span v-else class="grey--text">No category</span>
                      </td>
                      <td class="col-qty">
                        <div v-if="variantQtyBreakdown(product).length" class="qty-breakdown">
                          <div
                            v-for="(row, qi) in variantQtyBreakdown(product)"
                            :key="qi"
                            class="text-caption"
                          >
                            <span class="grey--text">{{ row.label }}:</span>
                            <span class="mono-figure" :class="row.quantity > 0 ? 'font-weight-medium' : 'error--text font-weight-medium'">{{ row.quantity }}</span>
                          </div>
                        </div>
                        <span v-else class="grey--text">-</span>
                      </td>
                      <td class="col-delivery">
                        {{ product.delivery_type == 0 ? "FREE DELIVERY" : "PAID DELIVERY" }}
                      </td>
                      <td class="col-delivery">
                        {{ product.delivery_amount || "FREE DELIVERY" }}
                      </td>
                      <td class="col-action text-right">
                        <template v-if="isDeletePage() != true">
                          <v-tooltip bottom v-if="can('PRODUCTS.DELETE')">
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn v-bind="attrs" v-on="on" fab x-small class="mr-1" color="error" @click="askDelete(product.id)">
                                <v-icon dark>mdi-delete</v-icon>
                              </v-btn>
                            </template>
                            <span>Delete this Product</span>
                          </v-tooltip>
                          <v-tooltip bottom v-if="can('PRODUCTS.UPDATE')">
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn v-bind="attrs" v-on="on" fab x-small class="mr-1" color="primary" @click="openEdit(product)">
                                <v-icon dark>mdi-pencil</v-icon>
                              </v-btn>
                            </template>
                            <span>Edit this Product</span>
                          </v-tooltip>
                          <v-tooltip bottom v-if="can('PRODUCTS.SINGLE_VIEW')">
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn v-bind="attrs" v-on="on" fab x-small class="mr-1" color="primary" @click="openView(product)">
                                <v-icon dark>mdi-eye</v-icon>
                              </v-btn>
                            </template>
                            <span>View Product Details</span>
                          </v-tooltip>
                          <v-tooltip bottom v-if="can('PRODUCTS.SINGLE_VIEW')">
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn v-bind="attrs" v-on="on" fab x-small color="success" :to="`/products/${encryptIt(product.id)}/qr-codes`">
                                <v-icon dark>mdi-qrcode</v-icon>
                              </v-btn>
                            </template>
                            <span>View QR Codes</span>
                          </v-tooltip>
                        </template>
                        <template v-else>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn v-bind="attrs" v-on="on" fab x-small color="error" @click="Restore_this(product.id)">
                                <v-icon dark>mdi-delete-restore</v-icon>
                              </v-btn>
                            </template>
                            <span>Restore this product</span>
                          </v-tooltip>
                        </template>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="10">
                        <AppEmptyState
                          icon="mdi-package-variant"
                          :title="isDeletePage() != true ? 'No products yet' : 'No deleted products'"
                          message="Products are added from a purchase order, when you buy stock in."
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <v-pagination rounded v-model="page" :length="paginationLength">
              </v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <!-- Clear Success Dialog Box -->
      <v-dialog v-model="is_deleted_product" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-empty
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Hurray!</h1>
                      <br />
                      <h3>product deleted successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_deleted_product = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
      <!-- Restore Success Dialog Box -->
      <v-dialog v-model="is_restored_product" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-restore
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Hurray!</h1>
                      <br />
                      <h3>Product restored successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_restored_product = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>

    <!-- Delete confirmation -->
    <ConfirmationDialog
      :show.sync="deleteDialog"
      title="Delete Product"
      :message="deleteMessage"
      icon="mdi-delete"
      color="error"
      :loading="delete_product || deletePreviewLoading"
      @confirm="Delete_this(pendingDeleteId)"
    >
      <template #details>
        <div v-if="deletePreviewLoading" class="d-flex align-center grey--text text-body-2">
          <v-progress-circular indeterminate size="16" width="2" class="mr-2"></v-progress-circular>
          Checking purchase orders...
        </div>
        <v-alert v-else-if="deletePreview && deletePreview.blocked" type="error" dense text class="text-body-2 mb-0">
          {{ deletePreview.blocked }}
        </v-alert>
        <div v-else-if="deletePreview && deletePreview.purchase_orders.length" class="text-body-2">
          <div class="font-weight-bold mb-2">It will also be removed from these purchase orders:</div>
          <div
            v-for="o in deletePreview.purchase_orders"
            :key="o.po_number"
            class="pd-po-row"
          >
            <div class="d-flex justify-space-between">
              <span class="font-weight-bold">{{ o.po_number }}<span v-if="o.is_draft" class="grey--text font-weight-regular"> (draft)</span></span>
              <span>{{ o.quantity }} pcs · ₹{{ money(o.amount) }}</span>
            </div>
            <div v-if="o.deletes_order" class="error--text text-caption">
              Nothing else is on this order - it is deleted with its bill<span v-if="o.paid > 0"> and ₹{{ money(o.paid) }} of payments</span>.
            </div>
            <div v-else-if="!o.is_draft" class="grey--text text-caption">
              Order total ₹{{ money(o.old_total) }} → ₹{{ money(o.new_total) }}<span v-if="o.payment_reduced > 0" class="warning--text">, payment reduced by ₹{{ money(o.payment_reduced) }}</span>
            </div>
          </div>
          <div class="grey--text text-caption mt-2">What is left in company stock is removed. Sales of it are kept as they are.</div>
        </div>
      </template>
    </ConfirmationDialog>

    <!-- View Product -->
    <AppDialog
      :show.sync="showViewDialog"
      max-width="960"
      icon="mdi-package-variant"
      :title="viewingProduct ? viewingProduct.name : ''"
      subtitle="Product details, variants and stock"
    >
      <ProductView
        v-if="showViewDialog && viewingProduct"
        :product-id="viewingProduct.id"
        :can-edit="can('PRODUCTS.UPDATE')"
        @edit="editFromView"
        @close="showViewDialog = false"
      />
    </AppDialog>

    <!-- Add / Edit Product -->
    <AppDialog
      :show.sync="showCreateDialog"
      max-width="1000"
      persistent
      icon="mdi-package-variant"
      title="Add Product"
      subtitle="Create a new product with its variants, pricing and category"
    >
      <ProductCreatePage
        v-if="showCreateDialog"
        embedded
        @created="onDialogSaved"
        @close="showCreateDialog = false"
      />
    </AppDialog>
    <AppDialog
      :show.sync="showEditDialog"
      max-width="1000"
      persistent
      icon="mdi-package-variant"
      title="Edit Product"
      subtitle="Update this product's details, pricing and variants"
    >
      <ProductEditPage
        v-if="showEditDialog"
        embedded
        :product-id="editingId"
        @saved="onDialogSaved"
        @close="showEditDialog = false"
      />
    </AppDialog>
  </v-main>
</template>

<script>
import axios from "axios";
import ConfirmationDialog from "@/components/ConfirmationDialog.vue";
import AppDialog from "@/components/AppDialog.vue";
import AppEmptyState from "@/components/AppEmptyState.vue";
import AppPageHeader from "@/components/AppPageHeader.vue";
import ProductCreatePage from "./create.vue";
import ProductEditPage from "./id.vue";
import ProductView from "./ProductView.vue";

export default {
  name: "productsPage",
  components: { ConfirmationDialog, AppDialog, AppEmptyState, AppPageHeader, ProductCreatePage, ProductEditPage, ProductView },
  data() {
    return {
      filter: {
        search_keyword: "",
        is_active: "",
        show_deleted: false,
        category_id: null,
      },
      products: [],
      deleteDialog: false,
      pendingDeleteId: null,
      deletePreview: null,
      deletePreviewLoading: false,
      showCreateDialog: false,
      showEditDialog: false,
      showViewDialog: false,
      viewingProduct: null,
      editingId: null,
      categoryLevels: [],
      statuses: [
        {
          id: 0,
          text: "Select Active Status",
          value: "",
        },
        { id: "ACTIVE", text: "ACTIVE", value: "1" },
        { id: "INACTIVE", text: "INACTIVE", value: "0" },
      ],

      page: 1,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      isLoading: false,
      // isExcelloading: false,
      delete_product: false,
      is_deleted_product: false,
      restore_product: false,
      is_restored_product: false,
    };
  },
  watch: {
    page: "getData",
    $route() {
      this.getData();
    },
  },
  mounted() {
    this.getData();
    this.loadRootCategories();
  },
  computed: {
    deleteMessage() {
      const p = this.deletePreview;
      if (p && p.blocked) return "This product can't be deleted right now.";
      return "Are you sure you want to delete this product?";
    },
  },
  methods: {
    getCategoryPath(category) {
      const path = [];
      let current = category;
      while (current) {
        path.unshift({ text: current.name });
        current = current.parent_category;
      }
      // Skip the root "Home" category when subcategories exist
      if (path.length > 1) path.shift();
      return path;
    },

    // One row per variant: "Color - Size: qty" (or just "Color" when the
    // variant has no size), sourced from Company Stock - the same stock
    // number shown on the Company Stocks / Stock Logs screens.
    variantQtyBreakdown(product) {
      const variants = product.product_variants || [];
      return variants.map((variant) => {
        const label = variant.size
          ? `${variant.color} - ${variant.size}`
          : variant.color || "Variant";
        return {
          label,
          // Laravel sends the companyStock relation as snake_case company_stock
          quantity: variant.company_stock ? variant.company_stock.quantity : 0,
        };
      });
    },

    async getData() {
      this.isLoading = true;
      this.filter.show_deleted = this.$route.query.open_trash ? 1 : 0;
      this.filter.search_keyword = this.filter.search_keyword
        ? this.filter.search_keyword
        : "";
      this.filter.is_active = this.filter.is_active
        ? this.filter.is_active
        : "";
      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.rowsPerPage +
        "&show_deleted=" +
        this.filter.show_deleted +
        "&is_active=" +
        this.filter.is_active +
        "&search_keyword=" +
        this.filter.search_keyword;

      if (this.filter.category_id) {
        query += "&category_id=" + this.filter.category_id;
      }
      await axios
        .get(`products?${query}`)
        .then((response) => {
          this.products = response.data.data;
          this.count = response.data.count;
          this.serialNoStarting = (this.page - 1) * this.rowsPerPage;
          this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
        })
        .catch(function (error) {
          console.log(error);
          // window.location.href = "/auth/login";
        });
      this.isLoading = false;
    },
    getCategoryLabel(levelIndex) {
      if (levelIndex === 0) return "Main Category";
      if (levelIndex === 1) return "Sub Category";
      const prefix = Array(levelIndex).fill("Sub").join(" ");
      return prefix + " Category";
    },

    async loadRootCategories() {
      try {
        const response = await axios.get("categories?is_active=1");
        if (response.data.success && response.data.data.length > 0) {
          this.categoryLevels = [
            { items: response.data.data, selected: null, loading: false },
          ];
        }
      } catch (error) {
        console.log(error);
      }
    },

    async onCategorySelect(levelIndex) {
      const selectedId = this.categoryLevels[levelIndex].selected;

      // Remove all levels deeper than the one being changed
      this.categoryLevels = this.categoryLevels.slice(0, levelIndex + 1);

      if (!selectedId) {
        // Cleared — revert filter to parent level's selected category (or none)
        this.filter.category_id =
          levelIndex > 0
            ? this.categoryLevels[levelIndex - 1].selected
            : null;
      } else {
        this.filter.category_id = selectedId;

        // Try loading subcategories for the selected category
        this.$set(this.categoryLevels[levelIndex], "loading", true);
        try {
          const response = await axios.get(
            `categories?parent_id=${selectedId}&is_active=1`
          );
          if (response.data.success && response.data.data.length > 0) {
            this.categoryLevels.push({
              items: response.data.data,
              selected: null,
              loading: false,
            });
          }
        } catch (error) {
          console.log(error);
        } finally {
          this.$set(this.categoryLevels[levelIndex], "loading", false);
        }
      }

      this.page = 1;
      this.getData();
    },

    openEdit(product) {
      this.editingId = this.encryptIt(product.id);
      this.showEditDialog = true;
    },
    openView(product) {
      this.viewingProduct = product;
      this.showViewDialog = true;
    },
    editFromView(product) {
      this.showViewDialog = false;
      this.openEdit(product);
    },
    onDialogSaved() {
      this.showCreateDialog = false;
      this.showEditDialog = false;
      this.getData();
      if (this.$toast) {
        this.$toast.success("Product saved successfully");
      }
    },
    // Deleting a product also takes it off its purchase orders - the
    // confirmation lists which ones and what happens to them
    async askDelete(id) {
      this.pendingDeleteId = id;
      this.deletePreview = null;
      this.deleteDialog = true;
      this.deletePreviewLoading = true;
      try {
        const res = await axios.get(`/products/${this.encryptIt(id)}/delete-preview`);
        this.deletePreview = res.data.data;
      } catch (e) {
        this.deletePreview = null;
      } finally {
        this.deletePreviewLoading = false;
      }
    },
    money(value) {
      return Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    async Delete_this(id) {
      this.delete_product = true;
      await axios
        .post(`/products/delete/${this.encryptIt(id)}`)
        .then(() => {
          this.is_deleted_product = true;
          this.deleteDialog = false;
          this.getData();
        })
        .catch((error) => {
          const message = error.response?.data?.message;
          if (this.$toast) this.$toast.error(message || "Failed to delete product");
        })
        .finally(() => {
          this.delete_product = false;
        });
    },
    async Restore_this(id) {
      this.restore_product = true;
      await axios
        .post(`/products/restore/${this.encryptIt(id)}`)
        .then(() => {
          this.restore_product = false;
          this.is_restored_product = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          // window.location.href = "/auth/login";
        });
    },
  },
};
</script>

<style scoped>
.pd-po-row {
  border-top: 1px dashed rgba(0, 0, 0, 0.12);
  padding: 6px 0;
}

.products-table-wrapper {
  width: 100%;
  overflow-x: auto;
  overflow-y: auto;
  max-height: 560px;
  border-radius: 8px;
}

.products-table {
  width: 100%;
  min-width: 1100px;
  border-collapse: collapse;
  font-size: 14px;
}

.products-table thead tr {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f5f5f5;
}

.products-table th {
  padding: 12px 14px;
  font-weight: 600;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
  border-bottom: 2px solid #e0e0e0;
  text-align: left;
}

.products-table td {
  padding: 10px 14px;
  vertical-align: middle;
  border-bottom: 1px solid #eeeeee;
}

.row-even { background: #ffffff; }
.row-odd  { background: #fafafa; }
.products-table tbody tr:hover { background: #f0f4ff; }

/* Column widths */
.col-sr       { width: 60px;  min-width: 60px;  white-space: nowrap; }
.col-status   { width: 100px; min-width: 90px;  white-space: nowrap; }
.col-name     { min-width: 130px; white-space: nowrap; }
.col-hsn      { width: 110px; min-width: 100px; white-space: nowrap; }
.col-desc     { min-width: 200px; max-width: 260px; }
.col-desc span {
  display: block;
  max-width: 240px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.col-category { min-width: 220px; }
.col-qty      { min-width: 130px; }
.col-delivery { min-width: 120px; white-space: nowrap; }
.col-action   { width: 160px; min-width: 150px; white-space: nowrap; }

.category-chips { display: flex; flex-wrap: wrap; gap: 4px; align-items: center; }
.arrow-sep { display: none; }
.qty-breakdown > div { line-height: 1.4; }

.text-right { text-align: right !important; }
</style>
